import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
  UsersIcon,
  UserGroupIcon,
  PhoneIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  Cog6ToothIcon,
  CreditCardIcon,
  ShieldCheckIcon,
  BellIcon,
  PaintBrushIcon,
  ServerIcon,
  DocumentTextIcon,
  CloudIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  XMarkIcon,
  EnvelopeIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import Layout from '../components/layout/Layout';
import { dashboardAPI, paymentsAPI, plansAPI, currenciesAPI } from '../services/api';
import { useCompany } from '../contexts/CompanyContext';
import { useSocket } from '../contexts/SocketContext';
import LoadingSpinner from '../components/common/LoadingSpinner';
import toast from 'react-hot-toast';
import api from '../services/api';

const CompanyAdminDashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [usageLoading, setUsageLoading] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [followupReminders, setFollowupReminders] = useState([]);
  const [showReminders, setShowReminders] = useState(false);
  const [showStorageDetailsModal, setShowStorageDetailsModal] = useState(false);
  const [storageDetails, setStorageDetails] = useState([]);
  const [storageDetailsLoading, setStorageDetailsLoading] = useState(false);

  // Payment management state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [plans, setPlans] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);
  const [selectedPlanForDetails, setSelectedPlanForDetails] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethodId: '',
    planId: '',
    currencyId: '',
    amount: '',
    transactionId: '',
    referenceNumber: '',
    notes: '',
    paymentProof: null
  });

  const navigate = useNavigate();
  const { 
    usage,
    companyData, 
    setUsage,
    setCompanyData,
    getStorageUsagePercentage, 
    formatStorageSize, 
    getSubscriptionStatus,
    getDaysUntilExpiry 
  } = useCompany();
  const { socket, isConnected } = useSocket();

  useEffect(() => {
    fetchDashboardData();
    fetchTeamMembers();
    fetchUsageData();
    fetchStatsData();
  }, []);

  // Socket event listeners for real-time updates
  useEffect(() => {
    if (!socket || !isConnected) return;

    const handleLeadCreated = (data) => {
      console.log('Lead created:', data);
      // Refresh stats data when lead is created
      fetchStatsData();
      fetchUsageData();
      toast.success('New lead created');
    };

    const handleLeadUpdated = (data) => {
      console.log('Lead updated:', data);
      // Refresh stats data when lead is updated
      fetchStatsData();
      fetchUsageData();
    };

    const handleCustomerCreated = (data) => {
      console.log('Customer created:', data);
      // Refresh stats data when customer is created
      fetchStatsData();
      fetchUsageData();
      toast.success('New customer created');
    };

    const handleCustomerUpdated = (data) => {
      console.log('Customer updated:', data);
      // Refresh stats data when customer is updated
      fetchStatsData();
      fetchUsageData();
    };

    const handleFollowupCreated = (data) => {
      console.log('Followup created:', data);
      // Refresh stats and dashboard data when followup is created
      fetchStatsData();
      fetchUsageData();
      fetchDashboardData();
      toast.success('New followup scheduled');
    };

    const handleFollowupUpdated = (data) => {
      console.log('Followup updated:', data);
      // Refresh stats and dashboard data when followup is updated
      fetchStatsData();
      fetchUsageData();
      fetchDashboardData();
    };

    // Register event listeners
    socket.on('lead-created', handleLeadCreated);
    socket.on('lead-updated', handleLeadUpdated);
    socket.on('customer-created', handleCustomerCreated);
    socket.on('customer-updated', handleCustomerUpdated);
    socket.on('followup-created', handleFollowupCreated);
    socket.on('followup-updated', handleFollowupUpdated);

    // Cleanup listeners on unmount
    return () => {
      socket.off('lead-created', handleLeadCreated);
      socket.off('lead-updated', handleLeadUpdated);
      socket.off('customer-created', handleCustomerCreated);
      socket.off('customer-updated', handleCustomerUpdated);
      socket.off('followup-created', handleFollowupCreated);
      socket.off('followup-updated', handleFollowupUpdated);
    };
  }, [socket, isConnected]);

  // Remove polling intervals - replaced with socket events
  // Auto-refresh usage data every 30 seconds
  // useEffect(() => {
  //   const usageInterval = setInterval(() => {
  //     fetchUsageData();
  //   }, 30000); // 30 seconds

  //   // Auto-refresh stats data every 15 seconds
  //   const statsInterval = setInterval(() => {
  //     fetchStatsData();
  //   }, 15000); // 15 seconds

  //   // Auto-refresh followup reminders every 60 seconds
  //   const remindersInterval = setInterval(() => {
  //     fetchDashboardData();
  //   }, 60000); // 60 seconds

  //   return () => {
  //     clearInterval(usageInterval);
  //     clearInterval(statsInterval);
  //     clearInterval(remindersInterval);
  //   };
  // }, []);

  const fetchUsageData = async () => {
    try {
      setUsageLoading(true);
      const response = await dashboardAPI.getCompanyUsage();
      setUsage(response.data.usage);
      setCompanyData(response.data.limits);
    } catch (error) {
      console.error('Error fetching usage data:', error);
    } finally {
      setUsageLoading(false);
    }
  };

  const fetchStatsData = async () => {
    try {
      setStatsLoading(true);
      const response = await dashboardAPI.getCompanyAdminDashboard();
      setStatsData({
        totalUsers: response.data.usage?.usersCount || 0,
        totalLeads: response.data.usage?.leadsCount || 0,
        totalCustomers: response.data.usage?.customersCount || 0,
        pendingFollowups: response.data.usage?.followupsCount || 0,
      });
    } catch (error) {
      console.error('Error fetching stats data:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchStorageDetails = async () => {
    try {
      setStorageDetailsLoading(true);
      const response = await dashboardAPI.getStorageDetails();
      setStorageDetails(response.data.storageDetails || []);
    } catch (error) {
      console.error('Storage details fetch error:', error);
      toast.error('Failed to load storage details');
    } finally {
      setStorageDetailsLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await dashboardAPI.getCompanyAdminDashboard();
      setDashboardData(response.data);

      // Update company data in context with subscription status from dashboard
      if (response.data.company) {
        setCompanyData(prevData => ({
          ...prevData,
          subscriptionStatus: response.data.company.subscriptionStatus,
          subscriptionEndDate: response.data.company.subscriptionEndDate
        }));
      }

      // Use the follow-ups returned by backend (today's and overdue)
      const followups = response.data.alerts?.upcomingFollowups || [];
      setFollowupReminders(followups.slice(0, 5)); // Limit to 5 for dashboard
      setShowReminders(followups.length > 0);
    } catch (error) {
      toast.error('Failed to load dashboard data');
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await api.get('/users', {
        params: {
          limit: 5,
          isActive: true
        }
      });
      setTeamMembers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const createSampleData = async () => {
    try {
      setIsCreating(true);
      const loadingToast = toast.loading('Creating sample master data...');
      const response = await api.post('/seed/master-data');
      toast.dismiss(loadingToast);
      
      if (response.data.success) {
        toast.success(
          `Sample data created successfully! Added ${response.data.data.products} products, ${response.data.data.leadStages} lead stages, and ${response.data.data.customerTypes} customer types.`,
          { duration: 5000 }
        );
        // Refresh dashboard data
        fetchDashboardData();
      }
    } catch (error) {
      toast.error('Failed to create sample data: ' + (error.response?.data?.message || error.message));
      console.error('Sample data creation error:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const getTimeUntilFollowup = (scheduledDate) => {
    const now = new Date();
    const scheduled = new Date(scheduledDate);
    const diffInMinutes = Math.floor((scheduled - now) / (1000 * 60));

    if (diffInMinutes < 0) return 'Overdue';

    if (diffInMinutes < 60) return `In ${diffInMinutes}m`;
    if (diffInMinutes < 1440) return `In ${Math.floor(diffInMinutes / 60)}h`;

    const diffInDays = Math.floor(diffInMinutes / 1440);
    return `In ${diffInDays}d`;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const getFollowupLink = (followup) => {
    console.log('Followup data:', followup);
    if (followup.relatedToModel === 'Lead' || followup.relatedToModel === 'lead') {
      const leadId = followup.relatedId?._id || followup.relatedId;
      console.log('Navigating to lead:', `/leads/${leadId}`);
      return `/leads/${leadId}`;
    } else if (followup.relatedToModel === 'Customer' || followup.relatedToModel === 'customer') {
      const customerId = followup.relatedId?._id || followup.relatedId;
      console.log('Navigating to customer:', `/customers/${customerId}`);
      return `/customers/${customerId}`;
    }
    console.log('No valid link found for followup');
    return null;
  };

  const getFollowupDisplayInfo = (followup) => {
    let name = 'Unknown Contact';
    let type = followup.type || 'followup';
    let priority = followup.priority || 'medium';

    if (followup.relatedId) {
      if (followup.relatedToModel === 'Customer' || type === 'customer') {
        // For customers
        if (followup.relatedId.firstName && followup.relatedId.lastName) {
          name = `${followup.relatedId.firstName} ${followup.relatedId.lastName}`;
        } else if (followup.relatedId.name) {
          name = followup.relatedId.name;
        } else if (followup.relatedId.companyName) {
          name = followup.relatedId.companyName;
        }
      } else if (followup.relatedToModel === 'Lead' || type === 'lead') {
        // For leads - check if it has customer data
        if (followup.relatedId.customer && followup.relatedId.customer.firstName) {
          name = `${followup.relatedId.customer.firstName} ${followup.relatedId.customer.lastName}`;
        } else if (followup.relatedId.firstName && followup.relatedId.lastName) {
          name = `${followup.relatedId.firstName} ${followup.relatedId.lastName}`;
        } else if (followup.relatedId.name) {
          name = followup.relatedId.name;
        }
      }
    }

    return { name, type, priority };
  };

  // Payment management functions
  const fetchPaymentData = async () => {
    try {
      const [paymentMethodsResponse, plansResponse, currenciesResponse] = await Promise.all([
        paymentsAPI.getActivePaymentMethods(),
        plansAPI.getCompanyActive(),
        currenciesAPI.getAll()
      ]);

      setPaymentMethods(paymentMethodsResponse.data.data || []);
      setPlans(plansResponse.data.data || []);
      setCurrencies(currenciesResponse.data.currencies || []);
    } catch (error) {
      console.error('Error fetching payment data:', error);
      toast.error('Failed to load payment options');
    }
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    try {
      const selectedPlan = plans.find(p => p._id === paymentForm.planId);
      const selectedCurrency = currencies.find(c => c._id === paymentForm.currencyId);

      if (!selectedPlan || !selectedCurrency) {
        toast.error('Please select a valid plan and currency');
        return;
      }

      const formData = new FormData();
      formData.append('paymentMethodId', paymentForm.paymentMethodId);
      formData.append('planId', paymentForm.planId);
      formData.append('currencyId', paymentForm.currencyId);
      formData.append('amount', paymentForm.amount);
      formData.append('transactionId', paymentForm.transactionId);
      formData.append('referenceNumber', paymentForm.referenceNumber);
      formData.append('notes', paymentForm.notes);

      if (paymentForm.paymentProof) {
        formData.append('paymentProof', paymentForm.paymentProof);
      }

      await paymentsAPI.createPayment(formData);

      toast.success('Payment submitted successfully! Waiting for approval.');
      setShowPaymentModal(false);
      setPaymentForm({
        paymentMethodId: '',
        planId: '',
        currencyId: '',
        amount: '',
        transactionId: '',
        referenceNumber: '',
        notes: '',
        paymentProof: null
      });
    } catch (error) {
      console.error('Error submitting payment:', error);
      toast.error('Failed to submit payment');
    }
  };

  const handleViewPlanDetails = (planId) => {
    const plan = plans.find(p => p._id === planId);
    setSelectedPlanForDetails(plan);
    setShowPlanDetailsModal(true);
  };

  const handlePlanChange = (planId) => {
    const selectedPlan = plans.find(p => p._id === planId);

    if (selectedPlan) {
      // Calculate discounted price if discount is available
      const originalPrice = selectedPlan.price;
      const discountPercentage = selectedPlan.discount || 0;
      const discountedPrice = originalPrice;

      // Set the currency to match the plan's currency and auto-fill discounted amount
      setPaymentForm({
        ...paymentForm,
        planId,
        currencyId: selectedPlan.currency._id,
        amount: discountedPrice.toFixed(2)
      });
    } else {
      setPaymentForm({
        ...paymentForm,
        planId
      });
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'call': return PhoneIcon;
      case 'meeting': return UsersIcon;
      case 'email': return EnvelopeIcon;
      case 'task': return CheckCircleIcon;
      default: return PhoneIcon;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'call': return 'from-blue-500 to-blue-600';
      case 'meeting': return 'from-purple-500 to-purple-600';
      case 'email': return 'from-green-500 to-green-600';
      case 'task': return 'from-orange-500 to-orange-600';
      case 'reminder': return 'from-indigo-500 to-indigo-600';
      case 'whatsapp': return 'from-emerald-500 to-emerald-600';
      default: return 'from-orange-500 to-orange-600';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <LoadingSpinner size="large" text="Loading dashboard..." />
        </div>
      </Layout>
    );
  }

  const stats = [
    {
      name: 'Total Users',
      value: statsData?.totalUsers || 0,
      icon: UsersIcon,
      color: 'from-blue-500 to-blue-600',
      limit: companyData?.plan?.maxUsers,
    },
    {
      name: 'Active Leads',
      value: statsData?.totalLeads || 0,
      icon: UserGroupIcon,
      color: 'from-green-500 to-emerald-600',
      limit: companyData?.plan?.maxLeads,
    },
    {
      name: 'Total Customers',
      value: statsData?.totalCustomers || 0,
      icon: UsersIcon,
      color: 'from-purple-500 to-violet-600',
      limit: companyData?.plan?.maxCustomers,
    },
    {
      name: 'Pending Follow-ups',
      value: statsData?.pendingFollowups || 0,
      icon: PhoneIcon,
      color: 'from-orange-500 to-red-600',
    },
  ];

  const subscriptionStatus = getSubscriptionStatus();
  const daysUntilExpiry = getDaysUntilExpiry();
  const storagePercentage = getStorageUsagePercentage();

  return (
    <Layout>
      <div className="p-3 md:p-4">
        {/* Modern Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative mb-4 md:mb-6 overflow-hidden rounded-2xl bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-700 p-3 md:p-4 text-white shadow-lg"
        >
          <div className="absolute inset-0 bg-black/10"></div>
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
          <div className="absolute -bottom-10 -left-10 h-32 w-32 rounded-full bg-white/5 blur-2xl"></div>
          
          <div className="relative z-10">
            <motion.div
              initial={{ x: -20, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center justify-between"
            >
              <div>
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  Company Dashboard
                </h1>
                <p className="text-blue-100 mt-1 text-xs md:text-sm">
                  Monitor your team's performance and company metrics
                </p>
              </div>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 200 }}
                className="hidden md:block"
              >
                <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl">
                  <ChartBarIcon className="h-12 w-12 text-white" />
                </div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Trial Period Highlight */}
        {subscriptionStatus === 'trial' && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.6
            }}
            className="mb-4"
          >
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 p-3 md:p-4 text-white shadow-lg">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-white/10 blur-xl"></div>
              <div className="absolute -bottom-4 -left-4 h-12 w-12 rounded-full bg-white/5 blur-lg"></div>

              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg shadow-sm">
                      <SparklesIcon className="h-5 w-5 text-white" />
                    </div>
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-bold">🎉 Trial Active</h3>
                    <p className="text-blue-100 text-xs">
                      {daysUntilExpiry > 0
                        ? `${daysUntilExpiry} days remaining`
                        : 'Trial ends today!'
                      }
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    fetchPaymentData();
                    setShowPaymentModal(true);
                  }}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 backdrop-blur-sm rounded-lg text-xs font-semibold transition-all duration-200 border border-white/30"
                >
                  Upgrade
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* Enhanced Alerts */}
        {(subscriptionStatus === 'expiring-soon' || subscriptionStatus === 'expired' || storagePercentage > 90) && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.95 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.6
            }}
            className="mb-4"
          >
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-red-500 via-orange-500 to-yellow-500 p-3 md:p-4 text-white shadow-lg">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-4 -right-4 h-16 w-16 rounded-full bg-white/10 blur-xl"></div>

              <div className="relative z-10 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <motion.div
                    animate={{
                      scale: [1, 1.2, 1],
                      rotate: [0, 10, -10, 0]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "loop"
                    }}
                    className="flex-shrink-0"
                  >
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg shadow-sm">
                      <ExclamationTriangleIcon className="h-5 w-5 text-white" />
                    </div>
                  </motion.div>
                  <div>
                    <h3 className="text-lg font-bold">Important Alerts</h3>
                    <p className="text-orange-100 text-xs">
                      {subscriptionStatus === 'expiring-soon' && `Expires in ${daysUntilExpiry} days`}
                      {subscriptionStatus === 'expired' && 'Subscription expired'}
                      {storagePercentage > 90 && `Storage at ${storagePercentage.toFixed(0)}%`}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Link
                    to="/company-settings"
                    className="px-3 py-1.5 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg text-xs font-medium transition-all duration-200"
                  >
                    Settings
                  </Link>
                  <button
                    onClick={() => {
                      fetchPaymentData();
                      setShowPaymentModal(true);
                    }}
                    className="px-3 py-1.5 bg-white/20 backdrop-blur-sm hover:bg-white/30 rounded-lg text-xs font-medium transition-all duration-200"
                  >
                    View Plans
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Subscription Expired - Block Access */}
        {subscriptionStatus === 'expired' && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
              duration: 0.6
            }}
            className="mb-4"
          >
            <div className="relative overflow-hidden rounded-xl bg-gradient-to-br from-gray-900 via-gray-800 to-black p-4 md:p-6 text-white shadow-lg">
              <div className="absolute inset-0 bg-black/50"></div>
              <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-red-500/10 blur-xl"></div>
              <div className="absolute -bottom-10 -left-10 h-24 w-24 rounded-full bg-red-500/5 blur-lg"></div>

              <div className="relative z-10 text-center">
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    rotate: [0, 5, -5, 0]
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    repeatType: "loop"
                  }}
                  className="flex justify-center mb-4"
                >
                  <div className="p-3 bg-red-500/20 backdrop-blur-sm rounded-xl shadow-lg">
                    <XCircleIcon className="h-8 w-8 text-red-400" />
                  </div>
                </motion.div>

                <h2 className="text-xl md:text-2xl font-bold mb-3 bg-gradient-to-r from-red-400 to-orange-400 bg-clip-text text-transparent">
                  Subscription Expired
                </h2>

                <p className="text-lg text-gray-300 mb-6 max-w-2xl mx-auto leading-relaxed">
                  Your trial period has ended and your subscription has expired. To continue using all features of the CRM platform, please renew your subscription.
                </p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    fetchPaymentData();
                    setShowPaymentModal(true);
                  }}
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-red-500 to-orange-500 hover:from-red-600 hover:to-orange-600 text-white text-sm font-semibold rounded-xl shadow-lg hover:shadow-red-500/25 transition-all duration-300"
                >
                  <CreditCardIcon className="h-5 w-5 mr-2" />
                  Renew Subscription Now
                </motion.button>

                <p className="text-xs text-gray-500 mt-4">
                  Need help? Contact our support team for assistance with your renewal.
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Main Dashboard Content - Only show if subscription is active or trial */}
        {(subscriptionStatus === 'active' || subscriptionStatus === 'trial') && (
        <>
        {/* Enhanced Stats Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-4 md:mb-6"
        >
          {/* Stats Header with Refresh */}
          <div className="col-span-full flex items-center justify-between mb-3">
            <h2 className="text-lg font-bold text-gray-900">Company Overview</h2>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={fetchStatsData}
              disabled={statsLoading}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs leading-4 font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              title="Refresh stats data"
            >
              <svg className={`h-3 w-3 mr-1.5 ${statsLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {statsLoading ? 'Refreshing...' : 'Refresh'}
            </motion.button>
          </div>

          {stats.map((stat, index) => (
            <motion.div
              key={stat.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.1 }}
              whileHover={{ 
                y: -8, 
                scale: 1.03,
                boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)"
              }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-500 relative group"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-transparent to-white/20 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-700"></div>
              
              <div className="relative p-3 md:p-4">
                <div className="flex items-center">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className={`p-2.5 rounded-lg shadow-sm bg-gradient-to-br ${stat.color} relative overflow-hidden`}
                  >
                    <div className="absolute inset-0 bg-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    <stat.icon className="h-5 w-5 text-white relative z-10" />
                  </motion.div>
                  <div className="ml-3 flex-1">
                    <p className="text-xs font-semibold text-gray-600 mb-1 group-hover:text-gray-800 transition-colors">{stat.name}</p>
                    <p className="text-2xl font-bold text-gray-900 mb-1 group-hover:text-gray-800 transition-colors">
                      {stat.value}
                      {stat.limit && (
                        <span className="text-sm text-gray-500 font-normal ml-1">
                          /{stat.limit}
                        </span>
                      )}
                    </p>
                    {stat.limit && (
                      <div className="w-full bg-gray-200 rounded-full h-3 mt-3 overflow-hidden shadow-inner">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${Math.min((stat.value / stat.limit) * 100, 100)}%` }}
                          transition={{ delay: 0.5 + index * 0.1, duration: 1, ease: "easeOut" }}
                          className={`h-3 rounded-full shadow-sm ${
                            (stat.value / stat.limit) * 100 > 90 ? 'bg-gradient-to-r from-red-500 via-red-600 to-red-700' : 
                            (stat.value / stat.limit) * 100 > 70 ? 'bg-gradient-to-r from-yellow-500 via-orange-500 to-orange-600' : 
                            'bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-600'
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-4">
          {/* Enhanced Recent Activity */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
          >
            <div className="bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500 px-3 md:px-4 py-3 md:py-4 relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-3 -right-3 h-12 w-12 rounded-full bg-white/10 blur-lg"></div>
              <div className="relative z-10">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <div className="p-1.5 bg-white/20 backdrop-blur-sm rounded-lg mr-2 shadow-sm">
                    <ClockIcon className="h-4 w-4 text-white" />
                  </div>
                  Recent Activity
                </h3>
              </div>
            </div>
            <div className="divide-y divide-gray-200 max-h-96 overflow-y-auto">
              {dashboardData?.recentActivity?.length > 0 ? (
                dashboardData.recentActivity.map((activity, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="px-3 md:px-4 py-3 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        {activity.type === 'lead' && (
                          <div className="h-10 w-10 bg-gradient-to-br from-green-400 to-green-600 rounded-xl flex items-center justify-center shadow-lg">
                            <UserGroupIcon className="h-5 w-5 text-white" />
                          </div>
                        )}
                        {activity.type === 'customer' && (
                          <div className="h-10 w-10 bg-gradient-to-br from-blue-400 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                            <UsersIcon className="h-5 w-5 text-white" />
                          </div>
                        )}
                        {activity.type === 'followup' && (
                          <div className="h-10 w-10 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
                            <PhoneIcon className="h-5 w-5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-gray-900">{activity.description}</p>
                        <p className="text-xs text-gray-500 flex items-center mt-1">
                          <ClockIcon className="h-3 w-3 mr-1" />
                          {activity.user} • {activity.time}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              ) : (
                <div className="px-4 md:px-6 py-12 text-center">
                  <div className="h-16 w-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <ClockIcon className="h-8 w-8 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm">No recent activity</p>
                  <p className="text-gray-400 text-xs mt-1">Activity will appear here as your team works</p>
                </div>
              )}
            </div>
          </motion.div>

          {/* Enhanced Team Performance */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden"
          >
            <div className="px-3 md:px-4 py-3 md:py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-gray-900 flex items-center">
                  <UsersIcon className="h-4 w-4 mr-2 text-purple-500" />
                  Team Members
                </h3>
                <Link 
                  to="/user-management"
                  className="text-purple-600 hover:text-purple-700 text-xs font-semibold hover:underline transition-all duration-200 flex items-center"
                >
                  <Cog6ToothIcon className="h-3 w-3 mr-1" />
                  Manage Users
                </Link>
              </div>
            </div>
            <div className="p-4 md:p-6">
              {teamMembers.length > 0 ? (
                <div className="space-y-4">
                  {teamMembers.slice(0, 4).map((member, index) => (
                    <motion.div
                      key={member._id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="flex items-center justify-between p-3 bg-gradient-to-r from-gray-50 to-white rounded-lg border border-gray-100 hover:shadow-sm transition-all duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="h-12 w-12 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-500 rounded-xl flex items-center justify-center shadow-lg">
                          <span className="text-sm font-bold text-white">
                            {member.firstName?.charAt(0)}{member.lastName?.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {member.firstName} {member.lastName}
                          </p>
                          <p className="text-xs text-gray-500 capitalize flex items-center">
                            <ShieldCheckIcon className="h-3 w-3 mr-1" />
                            {member.role === 'company_admin' ? 'Company Admin' : 'User'}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold mb-2 ${
                          member.isActive ? 'bg-green-100 text-green-800 border border-green-200' : 'bg-red-100 text-red-800 border border-red-200'
                        }`}>
                          <div className={`w-2 h-2 rounded-full mr-2 ${
                            member.isActive ? 'bg-green-500' : 'bg-red-500'
                          }`}></div>
                          {member.isActive ? 'Active' : 'Inactive'}
                        </div>
                        <p className="text-xs text-gray-500">
                          {member.lastLogin ? `Last login: ${new Date(member.lastLogin).toLocaleDateString()}` : 'Never logged in'}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                  {teamMembers.length > 4 && (
                    <div className="text-center pt-4 border-t border-gray-200">
                      <Link 
                        to="/user-management"
                        className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 text-sm font-semibold rounded-xl hover:from-purple-100 hover:to-purple-200 transition-all duration-200 border border-purple-200"
                      >
                        <UsersIcon className="h-4 w-4 mr-2" />
                        View all {teamMembers.length} team members
                      </Link>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="h-20 w-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <UsersIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <p className="text-gray-500 text-sm font-medium mb-2">No team members found</p>
                  <p className="text-gray-400 text-xs mb-4">Start building your team to get the most out of your CRM</p>
                  <Link 
                    to="/user-management"
                    className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white text-sm font-semibold rounded-xl hover:from-blue-600 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2" />
                    Add first team member
                  </Link>
                </div>
              )}
            </div>
          </motion.div>

          {/* Upcoming Follow-ups */}
          {showReminders && followupReminders.length > 0 && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-all duration-300"
            >
<div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 px-3 md:px-4 py-3 md:py-4 relative overflow-hidden">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="absolute -top-3 -right-3 h-12 w-12 rounded-full bg-white/10 blur-lg"></div>
              <div className="relative z-10">
                <h3 className="text-lg font-bold text-white flex items-center">
                  <div className="p-1.5 bg-white/20 backdrop-blur-sm rounded-lg mr-2 shadow-sm">
                    <ClockIcon className="h-4 w-4 text-white" />
                  </div>
                  Today's & Overdue Follow-ups
                  <span className="ml-2 bg-white/20 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full font-medium">
                    {followupReminders.length}
                  </span>
                </h3>
                <p className="text-orange-100 mt-0.5 text-xs">Click on any followup to view details</p>
                </div>
              </div>

              <div className="max-h-96 overflow-y-auto">
                {followupReminders.length > 0 ? (
                  followupReminders.map((followup, index) => {
                    const link = getFollowupLink(followup);
                    const { name, type, priority } = getFollowupDisplayInfo(followup);
                    const TypeIcon = getTypeIcon(type);
                    const isOverdue = new Date(followup.scheduledDate) < new Date();
                    return (
                      <motion.div
                        key={followup._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: 0.1 * index }}
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)"
                        }}
                        className={`p-3 md:p-4 border-b border-gray-100 last:border-b-0 transition-all duration-300 ${
                          followup.relatedToModel === 'Lead' || followup.relatedToModel === 'lead'
                            ? 'bg-gradient-to-r from-blue-50/50 to-indigo-50/50 border-l-4 border-l-blue-400'
                            : ''
                        } ${link ? 'cursor-pointer hover:bg-gradient-to-r hover:from-orange-50/80 hover:via-red-50/60 hover:to-pink-50/80 hover:shadow-lg hover:shadow-orange-500/10 hover:border-orange-200/50 group' : ''}`}
                        onClick={link ? () => {
                          console.log('Clicking followup, navigating to:', link);
                          // Pass followup ID as query parameter to auto-open and highlight
                          const url = new URL(link, window.location.origin);
                          url.searchParams.set('followupId', followup._id);
                          navigate(url.pathname + url.search);
                        } : undefined}
                      >
                        <div className="flex items-start space-x-3">
                          <motion.div
                            className={`flex-shrink-0 p-3 rounded-xl shadow-lg bg-gradient-to-br ${getTypeColor(type)} ring-2 ring-white/20 transition-all duration-300 group-hover:scale-110 group-hover:rotate-3 group-hover:shadow-xl group-hover:shadow-orange-500/30 group-hover:ring-orange-300/50`}
                            whileHover={{
                              scale: 1.15,
                              rotate: 6,
                              boxShadow: "0 10px 15px -3px rgba(249, 115, 22, 0.3), 0 4px 6px -2px rgba(249, 115, 22, 0.2)"
                            }}
                          >
                            <TypeIcon className="h-5 w-5 text-white drop-shadow-sm relative z-10" />
                          </motion.div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <motion.p
                                  className="text-sm font-semibold text-gray-900 group-hover:text-orange-700 transition-colors duration-300"
                                  whileHover={{ scale: 1.02 }}
                                >
                                  {followup.title || 'Follow-up'}
                                </motion.p>
                                <motion.p
                                  className="text-sm text-gray-600 mt-1 group-hover:text-gray-800 transition-colors duration-300"
                                  whileHover={{ scale: 1.01 }}
                                >
                                  {name}
                                </motion.p>
                                {/* Quick Action Button */}
                                <div className="mt-2 flex items-center space-x-2">
                                  {type === 'whatsapp' && (
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        console.log('WhatsApp button clicked for followup:', followup);
                                        console.log('relatedToModel:', followup.relatedToModel);
                                        console.log('relatedId:', followup.relatedId);

                                        // Get phone number based on followup type
                                        let phoneNumber = null;
                                        if (followup.relatedToModel === 'Customer') {
                                          // For customer followups, phone is directly on relatedId
                                          phoneNumber = followup.relatedId?.phone;
                                          console.log('Customer followup - phone from relatedId:', phoneNumber);
                                        } else if (followup.relatedToModel === 'Lead') {
                                          // For lead followups, phone might be on customer sub-object or directly on relatedId
                                          phoneNumber = followup.relatedId?.customer?.phone || followup.relatedId?.phone;
                                          console.log('Lead followup - phone from customer:', followup.relatedId?.customer?.phone);
                                          console.log('Lead followup - phone from relatedId:', followup.relatedId?.phone);
                                          console.log('Lead followup - final phone:', phoneNumber);
                                        }

                                        console.log('Final phone number:', phoneNumber);

                                        if (phoneNumber) {
                                          const cleanedNumber = phoneNumber.replace(/\D/g, '');
                                          const formattedNumber = cleanedNumber.startsWith('91') ? cleanedNumber : `91${cleanedNumber}`;
                                          console.log('Formatted number:', formattedNumber);
                                          // Create meaningful message based on followup type and title
                                          const followupTitle = followup.title && followup.title.trim()
                                            ? followup.title.trim()
                                            : (() => {
                                                switch (followup.type) {
                                                  case 'call': return 'our scheduled call';
                                                  case 'meeting': return 'our scheduled meeting';
                                                  case 'email': return 'our email follow-up';
                                                  case 'whatsapp': return 'our WhatsApp message';
                                                  case 'task': return 'our assigned task';
                                                  default: return 'our follow-up';
                                                }
                                              })();

                                          const message = `Hi ${name}, regarding: ${followupTitle}`;
                                          window.open(`https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`, '_blank');
                                        } else {
                                          toast.error('No phone number available for WhatsApp');
                                        }
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-green-100 text-green-800 hover:bg-green-200 transition-colors duration-200 shadow-sm"
                                    >
                                      <svg className="h-3 w-3 mr-1.5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488"/>
                                      </svg>
                                      Send WhatsApp
                                    </motion.button>
                                  )}
                                  {type === 'email' && (
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Get customer email
                                        const customerEmail = followup.relatedId?.customer?.email || followup.relatedId?.email;
                                        if (customerEmail) {
                                          const subject = encodeURIComponent(followup.title || 'Follow-up');
                                          const body = encodeURIComponent(`Hi ${name},\n\nRegarding: ${followup.description || followup.title || 'our follow-up'}\n\nBest regards`);
                                          window.location.href = `mailto:${customerEmail}?subject=${subject}&body=${body}`;
                                        } else {
                                          toast.error('No email address available');
                                        }
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors duration-200 shadow-sm"
                                    >
                                      <EnvelopeIcon className="h-3 w-3 mr-1.5" />
                                      Send Email
                                    </motion.button>
                                  )}
                                  {type === 'call' && (
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Get customer phone number
                                        const customerPhone = followup.relatedId?.customer?.phone || followup.relatedId?.phone;
                                        if (customerPhone) {
                                          window.location.href = `tel:${customerPhone}`;
                                        } else {
                                          toast.error('No phone number available for call');
                                        }
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-green-100 text-green-800 hover:bg-green-200 transition-colors duration-200 shadow-sm"
                                    >
                                      <PhoneIcon className="h-3 w-3 mr-1.5" />
                                      Call Now
                                    </motion.button>
                                  )}
                                  {type === 'meeting' && (
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Could integrate with calendar app or just show meeting details
                                        toast.info(`Meeting scheduled for ${formatDate(followup.scheduledDate)}`);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors duration-200 shadow-sm"
                                    >
                                      <CalendarDaysIcon className="h-3 w-3 mr-1.5" />
                                      View Meeting
                                    </motion.button>
                                  )}
                                  {type === 'task' && (
                                    <motion.button
                                      whileHover={{ scale: 1.05 }}
                                      whileTap={{ scale: 0.95 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toast.info(`Task: ${followup.title || 'Complete assigned task'}`);
                                      }}
                                      className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors duration-200 shadow-sm"
                                    >
                                      <CheckCircleIcon className="h-3 w-3 mr-1.5" />
                                      Complete Task
                                    </motion.button>
                                  )}
                                  {/* Show followup description if available */}
                                  {followup.description && (
                                    <div className="text-xs text-gray-500 max-w-xs truncate" title={followup.description}>
                                      {followup.description.length > 40 ? `${followup.description.substring(0, 40)}...` : followup.description}
                                    </div>
                                  )}
                                </div>
                              </div>
                              <div className="flex flex-col items-end space-y-1">
                                <motion.span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${getPriorityColor(priority)}`}
                                  whileHover={{ scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {priority.toUpperCase()}
                                </motion.span>
                                <motion.span
                                  className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                                    isOverdue ?
                                    'bg-red-100 text-red-800 shadow-sm' :
                                    'bg-orange-100 text-orange-800 shadow-sm'
                                  }`}
                                  whileHover={{ scale: 1.05, boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }}
                                  transition={{ duration: 0.2 }}
                                >
                                  {getTimeUntilFollowup(followup.scheduledDate)}
                                </motion.span>
                                <motion.p
                                  className="text-xs text-gray-500 font-medium group-hover:text-gray-700 transition-colors duration-300"
                                  whileHover={{ scale: 1.02 }}
                                >
                                  {formatDate(followup.scheduledDate)}
                                </motion.p>
                              </div>
                            </div>
                            {followup.assignedTo && (
                              <motion.div
                                className="flex items-center mt-2"
                                whileHover={{ scale: 1.02 }}
                              >
                                <motion.div
                                  className="h-5 w-5 bg-gradient-to-br from-blue-400 to-blue-600 rounded-full flex items-center justify-center shadow-sm"
                                  whileHover={{
                                    scale: 1.1,
                                    boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.3)"
                                  }}
                                  transition={{ duration: 0.2 }}
                                >
                                  <span className="text-xs text-white font-semibold">
                                    {followup.assignedTo.firstName?.[0]}{followup.assignedTo.lastName?.[0]}
                                  </span>
                                </motion.div>
                                <span className="text-xs text-gray-600 ml-2 group-hover:text-gray-800 transition-colors duration-300">
                                  Assigned to {followup.assignedTo.firstName} {followup.assignedTo.lastName}
                                </span>
                              </motion.div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                ) : (
                  <div className="text-center py-12">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="mx-auto w-20 h-20 bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center shadow-lg mb-4"
                    >
                      <ClockIcon className="h-10 w-10 text-gray-400" />
                    </motion.div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No upcoming follow-ups</h3>
                    <p className="text-gray-500 text-sm">All caught up! No follow-ups scheduled for today.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </div>

        {/* Enhanced Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-4 md:mt-6 bg-gradient-to-br from-white via-gray-50 to-blue-50 rounded-xl shadow-lg border border-gray-200 p-3 md:p-4 overflow-hidden relative"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-purple-500/5 to-indigo-500/5"></div>
          <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-gradient-to-br from-blue-400/10 to-purple-400/10 blur-3xl"></div>
          
          <div className="relative z-10">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="text-center mb-4"
            >
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-1">Quick Actions</h3>
              <p className="text-gray-600 text-sm">Powerful tools to manage your business efficiently</p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3 md:gap-4">
              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/user-management"
                  className="flex items-center p-3 md:p-4 bg-gradient-to-br from-blue-50 via-blue-100 to-indigo-100 rounded-lg border border-blue-200 hover:shadow-lg hover:border-blue-300 transition-all duration-300 group block h-full"
                >
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg mr-3 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                    <UsersIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-0.5 text-sm">Manage Users</p>
                    <p className="text-xs text-gray-600 leading-tight">Add, edit, or remove team members</p>
                  </div>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="h-5 w-5 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/team-analytics"
                  className="flex items-center p-3 md:p-4 bg-gradient-to-br from-purple-50 via-purple-100 to-pink-100 rounded-lg border border-purple-200 hover:shadow-lg hover:border-purple-300 transition-all duration-300 group block h-full"
                >
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg mr-3 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                    <ChartBarIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-0.5 text-sm">View Analytics</p>
                    <p className="text-xs text-gray-600 leading-tight">Track team performance</p>
                  </div>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="h-5 w-5 text-purple-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/leads"
                  className="flex items-center p-3 md:p-4 bg-gradient-to-br from-green-50 via-green-100 to-emerald-100 rounded-lg border border-green-200 hover:shadow-lg hover:border-green-300 transition-all duration-300 group block h-full"
                >
                  <div className="p-2 bg-gradient-to-br from-green-500 to-green-600 rounded-lg mr-3 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                    <UserGroupIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-0.5 text-sm">Manage Leads</p>
                    <p className="text-xs text-gray-600 leading-tight">View and manage all leads</p>
                  </div>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Link
                  to="/company-settings"
                  className="flex items-center p-3 md:p-4 bg-gradient-to-br from-orange-50 via-orange-100 to-red-100 rounded-lg border border-orange-200 hover:shadow-lg hover:border-orange-300 transition-all duration-300 group block h-full"
                >
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg mr-3 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                    <Cog6ToothIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-0.5 text-sm">Settings</p>
                    <p className="text-xs text-gray-600 leading-tight">Configure company preferences</p>
                  </div>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="h-5 w-5 text-orange-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </Link>
              </motion.div>

              <motion.div
                whileHover={{ y: -5, scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="md:col-span-2 lg:col-span-1"
              >
                <button
                  onClick={() => {
                    fetchPaymentData();
                    setShowPaymentModal(true);
                  }}
                  className="flex items-center p-3 md:p-4 bg-gradient-to-br from-emerald-50 via-emerald-100 to-teal-100 rounded-lg border border-emerald-200 hover:shadow-lg hover:border-emerald-300 transition-all duration-300 group w-full h-full"
                >
                  <div className="p-2 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg mr-3 group-hover:scale-105 transition-transform duration-200 shadow-sm">
                    <CurrencyDollarIcon className="h-4 w-4 text-white" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 mb-0.5 text-sm">Make Payment</p>
                    <p className="text-xs text-gray-600 leading-tight">Upgrade or renew your subscription</p>
                  </div>
                  <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="h-5 w-5 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </button>
              </motion.div>

         
            </div>
          </div>
        </motion.div>

        {/* Subscription Information */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="mt-8 space-y-8"
        >
          {/* Plan Overview */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <div className="p-2 bg-blue-500 rounded-lg">
                  <CreditCardIcon className="h-5 w-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Subscription Plan</h3>
                  <p className="text-gray-600 text-sm">Your current plan and billing information</p>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full text-sm font-semibold ${
                subscriptionStatus === 'active' ? 'bg-green-100 text-green-800' :
                subscriptionStatus === 'trial' ? 'bg-blue-100 text-blue-800' :
                subscriptionStatus === 'expiring-soon' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {subscriptionStatus === 'active' ? 'Active' :
                 subscriptionStatus === 'trial' ? 'Trial' :
                 subscriptionStatus === 'expiring-soon' ? 'Expiring Soon' :
                 subscriptionStatus === 'expired' ? 'Expired' : 'Unknown'}
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Plan Details */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <ServerIcon className="h-4 w-4 mr-2 text-primary" />
                  Plan Details
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan Name:</span>
                    <span className="font-semibold text-gray-900">{companyData?.plan?.name || 'No plan'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      {companyData?.plan?.billingCycle === 'yearly' ? 'Yearly Price:' : 'Monthly Price:'}
                    </span>
                    <span className="font-semibold text-gray-900">
                      {companyData?.currency?.symbol || '$'}{companyData?.plan?.price || 0}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Billing Cycle:</span>
                    <span className="font-semibold text-gray-900 capitalize">
                      {companyData?.plan?.billingCycle || 'monthly'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Currency:</span>
                    <span className="font-semibold text-gray-900">{companyData?.currency?.code || 'USD'}</span>
                  </div>
                </div>
              </div>

              {/* Subscription Dates */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <CalendarDaysIcon className="h-4 w-4 mr-2 text-green-500" />
                  Subscription Period
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Start Date:</span>
                    <span className="font-semibold text-gray-900">
                      {companyData?.subscriptionStartDate ? new Date(companyData.subscriptionStartDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">End Date:</span>
                    <span className="font-semibold text-gray-900">
                      {companyData?.subscriptionEndDate ? new Date(companyData.subscriptionEndDate).toLocaleDateString() : 'N/A'}
                    </span>
                  </div>
                  {daysUntilExpiry !== null && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Days Remaining:</span>
                      <span className={`font-semibold ${daysUntilExpiry <= 30 ? 'text-red-600' : 'text-gray-900'}`}>
                        {daysUntilExpiry} days
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Auto Renewal:</span>
                    <span className="font-semibold text-gray-900">Enabled</span>
                  </div>
                </div>
              </div>

              {/* Plan Description */}
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <DocumentTextIcon className="h-4 w-4 mr-2 text-purple-500" />
                  Plan Features
                </h4>
                <p className="text-gray-600 text-sm mb-4">
                  {companyData?.plan?.description || 'No description available'}
                </p>
                <div className="space-y-2">
                  <div className="flex items-center text-sm">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <span>Lead Management</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <span>Customer Tracking</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <span>Follow-up Automation</span>
                  </div>
                  <div className="flex items-center text-sm">
                    <CheckCircleIcon className="h-4 w-4 text-green-500 mr-2" />
                    <span>File Attachments</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Usage Metrics */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Resource Usage */}
            <div className="bg-white rounded-lg shadow-sm p-4">
              <div className="flex items-center justify-between mb-4">
                <h4 className="text-base font-semibold text-gray-900 flex items-center">
                  <ChartBarIcon className="h-4 w-4 mr-2 text-primary" />
                  Resource Usage
                </h4>
                <button
                  onClick={fetchUsageData}
                  disabled={usageLoading}
                  className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh usage data"
                >
                  <svg className={`h-4 w-4 mr-2 ${usageLoading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  {usageLoading ? 'Refreshing...' : 'Refresh'}
                </button>
              </div>
              <div className="space-y-6">
                {/* Storage Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Storage Used</span>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          fetchStorageDetails();
                          setShowStorageDetailsModal(true);
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                        title="View detailed storage breakdown"
                      >
                        Show Details
                      </button>
                      <span className="font-medium text-gray-900">
                        {(usage?.totalStorage || 0)} / {formatStorageSize(companyData?.plan?.storageLimitMB || 0)}
                      </span>
                    </div>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        storagePercentage >= 100 ? 'bg-red-500' :
                        storagePercentage > 80 ? 'bg-yellow-500' : 'bg-primary'
                      }`}
                      style={{ width: `${Math.min(storagePercentage, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{storagePercentage.toFixed(1)}% used</span>
                    <span>{formatStorageSize((companyData?.plan?.storageLimitMB || 0) - (usage?.storageUsedMB || 0))} remaining</span>
                  </div>
                </div>

                {/* Users Usage */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Team Members</span>
                    <span className="font-medium text-gray-900">
                      {usage?.usersCount || 0} / {companyData?.plan?.maxUsers || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        ((usage?.usersCount || 0) / (companyData?.plan?.maxUsers || 1)) * 100 >= 100 ? 'bg-red-500' :
                        ((usage?.usersCount || 0) / (companyData?.plan?.maxUsers || 1)) * 100 > 80 ? 'bg-yellow-500' : 'bg-primary'
                      }`}
                      style={{ width: `${Math.min(((usage?.usersCount || 0) / (companyData?.plan?.maxUsers || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{(((usage?.usersCount || 0) / (companyData?.plan?.maxUsers || 1)) * 100).toFixed(1)}% used</span>
                    <span>{(companyData?.plan?.maxUsers || 0) - (usage?.usersCount || 0)} remaining</span>
                  </div>
                </div>

                {/* Leads Usage */}
                {/* <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Active Leads</span>
                    <span className="font-medium text-gray-900">
                      {usage?.leadsCount || 0} / {companyData?.plan?.maxLeads || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        ((usage?.leadsCount || 0) / (companyData?.plan?.maxLeads || 1)) * 100 >= 100 ? 'bg-red-500' :
                        ((usage?.leadsCount || 0) / (companyData?.plan?.maxLeads || 1)) * 100 > 80 ? 'bg-yellow-500' : 'bg-primary'
                      }`}
                      style={{ width: `${Math.min(((usage?.leadsCount || 0) / (companyData?.plan?.maxLeads || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{(((usage?.leadsCount || 0) / (companyData?.plan?.maxLeads || 1)) * 100).toFixed(1)}% used</span>
                    <span>{(companyData?.plan?.maxLeads || 0) - (usage?.leadsCount || 0)} remaining</span>
                  </div>
                </div> */}

                {/* Customers Usage */}
                {/* <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-gray-600">Total Customers</span>
                    <span className="font-medium text-gray-900">
                      {usage?.customersCount || 0} / {companyData?.plan?.maxCustomers || 0}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className={`h-3 rounded-full transition-all duration-300 ${
                        ((usage?.customersCount || 0) / (companyData?.plan?.maxCustomers || 1)) * 100 >= 100 ? 'bg-red-500' :
                        ((usage?.customersCount || 0) / (companyData?.plan?.maxCustomers || 1)) * 100 > 80 ? 'bg-yellow-500' : 'bg-primary'
                      }`}
                      style={{ width: `${Math.min(((usage?.customersCount || 0) / (companyData?.plan?.maxCustomers || 1)) * 100, 100)}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>{(((usage?.customersCount || 0) / (companyData?.plan?.maxCustomers || 1)) * 100).toFixed(1)}% used</span>
                    <span>{(companyData?.plan?.maxCustomers || 0) - (usage?.customersCount || 0)} remaining</span>
                  </div>
                </div> */}
              </div>
            </div>

            {/* Enabled Features */}
            {/* <div className="bg-white rounded-xl shadow-sm p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                <ShieldCheckIcon className="h-5 w-5 mr-2 text-green-500" />
                Enabled Features
              </h4>
              <div className="grid grid-cols-1 gap-4">
                {companyData?.plan?.enabledModules && (
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Modules</h5>
                    <div className="space-y-2">
                      {Object.entries(companyData.plan.enabledModules).map(([module, enabled]) => (
                        <div key={module} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 capitalize">{module}</span>
                          {enabled ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {companyData?.settings?.features && (
                  <div className="border-t pt-4">
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Advanced Features</h5>
                    <div className="space-y-2">
                      {Object.entries(companyData.settings.features).map(([feature, enabled]) => (
                        <div key={feature} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">
                            {feature.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                          </span>
                          {enabled ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          ) : (
                            <XCircleIcon className="h-4 w-4 text-red-500" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div> */}
          </div>

          {/* Company Settings Overview */}
          {/* <div className="bg-white rounded-xl shadow-sm p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
              <Cog6ToothIcon className="h-5 w-5 mr-2 text-gray-500" />
              Company Settings
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
           
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mb-3">
                  <BellIcon className="h-6 w-6 text-blue-600" />
                </div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Notifications</h5>
                <div className="space-y-1 text-xs text-gray-600">
                  <div className="flex items-center justify-center">
                    <span>Email alerts:</span>
                    <CheckCircleIcon className="h-3 w-3 text-green-500 ml-1" />
                  </div>
                  <div className="flex items-center justify-center">
                    <span>SMS alerts:</span>
                    <XCircleIcon className="h-3 w-3 text-red-500 ml-1" />
                  </div>
                </div>
              </div>

           
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-lg mb-3">
                  <ShieldCheckIcon className="h-6 w-6 text-red-600" />
                </div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Security</h5>
                <div className="space-y-1 text-xs text-gray-600">
                  <div>2FA: Disabled</div>
                  <div>Session: 60 min</div>
                </div>
              </div>

            
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-purple-100 rounded-lg mb-3">
                  <PaintBrushIcon className="h-6 w-6 text-purple-600" />
                </div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Branding</h5>
                <div className="space-y-1 text-xs text-gray-600">
                  <div>Theme: Default</div>
                  <div>Colors: Custom</div>
                </div>
              </div>

          
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-lg mb-3">
                  <CloudIcon className="h-6 w-6 text-green-600" />
                </div>
                <h5 className="text-sm font-medium text-gray-900 mb-2">Integrations</h5>
                <div className="space-y-1 text-xs text-gray-600">
                  <div>Email: Enabled</div>
                  <div>API: Available</div>
                </div>
              </div>
            </div>
          </div> */}
        </motion.div>
        </>
        )}
      </div>

      {/* Payment Modal */}
      <AnimatePresence>
        {showPaymentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-hidden border border-gray-200/50"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modern Header */}
              <div className="relative bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 p-8 text-white overflow-hidden">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute -top-20 -right-20 h-40 w-40 rounded-full bg-white/10 blur-3xl"></div>
                <div className="absolute -bottom-20 -left-20 h-32 w-32 rounded-full bg-white/5 blur-2xl"></div>

                <div className="relative z-10 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                      className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl shadow-xl"
                    >
                      <CurrencyDollarIcon className="h-8 w-8 text-white" />
                    </motion.div>
                    <div>
                      <motion.h2
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.3 }}
                        className="text-3xl font-bold bg-gradient-to-r from-white to-emerald-100 bg-clip-text text-transparent"
                      >
                        Make Payment
                      </motion.h2>
                      <motion.p
                        initial={{ x: -20, opacity: 0 }}
                        animate={{ x: 0, opacity: 1 }}
                        transition={{ delay: 0.4 }}
                        className="text-emerald-100 mt-1"
                      >
                        Secure payment processing for your subscription
                      </motion.p>
                    </div>
                  </div>
                  <motion.button
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPaymentModal(false)}
                    className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl text-white hover:bg-white/30 transition-all duration-300 shadow-lg"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </motion.button>
                </div>
              </div>

              <div className="max-h-[70vh] overflow-y-auto">
                <form onSubmit={handlePaymentSubmit} className="p-8 space-y-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Plan *
                    </label>
                    <div className="flex gap-3">
                      <select
                        required
                        value={paymentForm.planId}
                        onChange={(e) => handlePlanChange(e.target.value)}
                        className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      >
                        <option value="">Choose a plan</option>
                        {plans.length === 0 ? (
                          <option disabled>No plans available for your country</option>
                        ) : (
                          plans.map(plan => {
                            const originalPrice = plan.price;
                            const discountPercentage = plan.discount || 0;
                            const discountedPrice = originalPrice;

                            return (
                              <option key={plan._id} value={plan._id}>
                                {plan.name} - {plan.currency?.symbol || '$'}
                                {discountPercentage > 0
                                  ? `${discountedPrice}/${plan.billingCycle === 'yearly' ? 'year' : 'month'} (${discountPercentage}% off from ${plan.currency?.symbol || '$'}${originalPrice / (discountPercentage / 100)})`
                                  : `${originalPrice}/${plan.billingCycle === 'yearly' ? 'year' : 'month'}`
                                }
                              </option>
                            );
                          })
                        )}
                      </select>
                      {paymentForm.planId && (
                        <button
                          type="button"
                          onClick={() => handleViewPlanDetails(paymentForm.planId)}
                          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-hover transition-colors flex items-center gap-2"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                          View Plan
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Currency *
                    </label>
                    <select
                      required
                      value={paymentForm.currencyId}
                      onChange={(e) => setPaymentForm({ ...paymentForm, currencyId: e.target.value })}
                      disabled={!!paymentForm.planId}
                      className={`w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent ${
                        paymentForm.planId ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                    >
                      <option value="">Select currency</option>
                      {(currencies || []).map(currency => (
                        <option key={currency._id} value={currency._id}>
                          {currency.code} ({currency.symbol})
                        </option>
                      ))}
                    </select>
                    {paymentForm.planId && (
                      <p className="text-xs text-gray-500 mt-1">
                        Currency is automatically set based on the selected plan
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Amount *
                    </label>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0"
                      value={paymentForm.amount}
                      disabled
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-700 cursor-not-allowed"
                      placeholder="Amount will be calculated based on selected plan"
                    />
                    {paymentForm.planId && (
                      <p className="text-xs text-green-600 mt-1">
                        ✓ Amount auto-calculated with discount applied
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Payment Method *
                    </label>
                    <select
                      required
                      value={paymentForm.paymentMethodId}
                      onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethodId: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    >
                      <option value="">Select payment method</option>
                      {paymentMethods.map(method => (
                        <option key={method._id} value={method._id}>
                          {method.name} ({method.type})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Transaction ID
                    </label>
                    <input
                      type="text"
                      value={paymentForm.transactionId}
                      onChange={(e) => setPaymentForm({ ...paymentForm, transactionId: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Transaction/Reference ID"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={paymentForm.referenceNumber}
                      onChange={(e) => setPaymentForm({ ...paymentForm, referenceNumber: e.target.value })}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      placeholder="Reference number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Proof (Screenshot/Image)
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentProof: e.target.files[0] })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {paymentForm.paymentProof && (
                    <p className="text-sm text-gray-600 mt-1">
                      Selected: {paymentForm.paymentProof.name}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>
                  <textarea
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    rows={3}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Any additional notes..."
                  />
                </div>

                {/* Payment Methods Display */}
                {paymentMethods.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-3">Available Payment Methods</h4>
                    <div className="space-y-3">
                      {paymentMethods.map(method => (
                        <div key={method._id} className="bg-white rounded-lg p-3 border border-gray-200">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                method.type === 'bank' ? 'bg-blue-500' :
                                method.type === 'upi' ? 'bg-green-500' :
                                method.type === 'wallet' ? 'bg-purple-500' : 'bg-orange-500'
                              }`}>
                                <span className="text-white font-bold text-xs">
                                  {method.type === 'bank' ? 'B' :
                                   method.type === 'upi' ? 'U' :
                                   method.type === 'wallet' ? 'W' : 'C'}
                                </span>
                              </div>
                              <div>
                                <h5 className="font-medium text-gray-900">{method.name}</h5>
                                <p className="text-sm text-gray-600 capitalize">{method.type}</p>
                              </div>
                            </div>
                            {method.type === 'upi' && method.upiDetails?.qrCode && (
                              <div className="text-right">
                                <img
                                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}${method.upiDetails.qrCode.url}`}
                                  alt="QR Code"
                                  className="w-40 h-40 object-contain border border-gray-200 rounded-lg shadow-lg"
                                />
                              </div>
                            )}
                          </div>

                          {/* Bank Details */}
                          {method.type === 'bank' && method.bankDetails && (
                            <div className="mt-3 text-sm text-gray-600">
                              <p><strong>Bank:</strong> {method.bankDetails.bankName}</p>
                              <p><strong>Account:</strong> {method.bankDetails.accountNumber}</p>
                              <p><strong>IFSC:</strong> {method.bankDetails.ifscCode}</p>
                            </div>
                          )}

                          {/* UPI Details */}
                          {method.type === 'upi' && method.upiDetails && (
                            <div className="mt-3 text-sm text-gray-600">
                              <p><strong>UPI ID:</strong> {method.upiDetails.upiId}</p>
                            </div>
                          )}

                          {/* Wallet Details */}
                          {method.type === 'wallet' && method.walletDetails && (
                            <div className="mt-3 text-sm text-gray-600">
                              <p><strong>Wallet:</strong> {method.walletDetails.walletName}</p>
                              <p><strong>Address:</strong> {method.walletDetails.walletAddress}</p>
                            </div>
                          )}

                          {/* Crypto Details */}
                          {method.type === 'crypto' && method.cryptoDetails && (
                            <div className="mt-3 text-sm text-gray-600">
                              <p><strong>Currency:</strong> {method.cryptoDetails.currency}</p>
                              <p><strong>Network:</strong> {method.cryptoDetails.network}</p>
                              <p><strong>Address:</strong> {method.cryptoDetails.walletAddress}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="button"
                    onClick={() => setShowPaymentModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Cancel
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 border border-transparent rounded-xl hover:from-emerald-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Submit Payment
                  </motion.button>
                </div>
                </form>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Plan Details Modal */}
      <AnimatePresence>
        {showPlanDetailsModal && selectedPlanForDetails && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-2xl">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                      <DocumentTextIcon className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Plan Details</h3>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowPlanDetailsModal(false)}
                    className="p-2 bg-white/20 backdrop-blur-sm rounded-lg text-white hover:bg-white/30 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </motion.button>
                </div>
              </div>

              <div className="p-6">
                <div className="mb-6">
                  <h4 className="text-2xl font-bold text-gray-900 mb-2">{selectedPlanForDetails.name}</h4>
                  <p className="text-gray-600">{selectedPlanForDetails.description}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-3">Pricing Information</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{selectedPlanForDetails.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'} Price:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPlanForDetails.currency?.symbol || '$'}{selectedPlanForDetails.price}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Billing Cycle:</span>
                        <span className="font-semibold text-gray-900">{selectedPlanForDetails.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Currency:</span>
                        <span className="font-semibold text-gray-900">
                          {selectedPlanForDetails.currency?.code || 'USD'}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-3">Plan Limits</h5>
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Max Users:</span>
                        <span className="font-semibold text-gray-900">{selectedPlanForDetails.maxUsers}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Storage Limit:</span>
                        <span className="font-semibold text-gray-900">{selectedPlanForDetails.storageLimitMB} MB</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Country:</span>
                        <span className="font-semibold text-gray-900">{selectedPlanForDetails.country?.name || 'Global'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {selectedPlanForDetails.features && selectedPlanForDetails.features.length > 0 && (
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h5 className="font-semibold text-gray-900 mb-3">Features Included</h5>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {selectedPlanForDetails.features.map((feature, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <CheckCircleIcon className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex justify-end mt-6">
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowPlanDetailsModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-gray-700 bg-gray-100 border border-gray-300 rounded-xl hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Storage Details Modal */}
      <AnimatePresence>
        {showStorageDetailsModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowStorageDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="relative w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white rounded-3xl shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="relative bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700 p-6 text-white">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full bg-white/10 blur-3xl"></div>
                <div className="relative z-10 flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold">Storage Usage Details</h2>
                    <p className="text-blue-100 mt-1">Detailed breakdown of your company's storage usage</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1, rotate: 90 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowStorageDetailsModal(false)}
                    className="p-2 hover:bg-white/20 rounded-xl transition-colors duration-200"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </motion.button>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 max-h-96 overflow-y-auto">
                {storageDetailsLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <LoadingSpinner size="large" text="Loading storage details..." />
                  </div>
                ) : storageDetails.length > 0 ? (
                  <div className="space-y-4">
                    {storageDetails.map((detail, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="flex items-center justify-between p-4 bg-gradient-to-r from-gray-50 to-white rounded-xl border border-gray-200 hover:shadow-md transition-all duration-200"
                      >
                        <div className="flex items-center space-x-4">
                          <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
                            <ServerIcon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <p className="font-semibold text-gray-900 text-lg">{detail.split(' => ')[0]}</p>
                            <p className="text-2xl font-bold text-blue-600">{detail.split(' => ')[1]}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: '100%' }}
                              transition={{ delay: index * 0.1, duration: 0.8 }}
                              className="h-full bg-gradient-to-r from-blue-500 to-purple-600 rounded-full"
                            />
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <ServerIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500 text-lg">No storage details available</p>
                    <p className="text-gray-400 text-sm mt-2">Unable to load storage breakdown</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <p>Total Storage Used: <span className="font-semibold text-gray-900">{(usage?.totalStorage || 0) || 'N/A'}</span></p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowStorageDetailsModal(false)}
                    className="px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Layout>
  );
};

export default CompanyAdminDashboard;
